import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { TabType } from '@innovamat/glimmer-components';
import { useQueryParams } from '@innovamat/hooks';
import { sortClassrooms, Stage, stages } from '@innovamat/radiance-utils';

import { useCourses } from '../../../../shared';
import { useUser } from '../../../../user-management';
import { useClassrooms } from '../../../hooks/use-classrooms';
import type { Classroom } from '../../../modules/classroom';
import { CLASSROOM_TYPE } from '../../../modules/classroom';
import { filterAndSortClassrooms } from '../../../modules/classroom/classrooms';

const CLASSROOM_TABS = {
  ...Stage,
} as const;

type GroupedCourses = {
  stage: string;
  courses: any[];
};

type ClassroomSelectedTab =
  (typeof CLASSROOM_TABS)[keyof typeof CLASSROOM_TABS];

type UseSchoolClassroomsDataReturn = {
  filteredClassrooms: Classroom[];
  groupedCourses: GroupedCourses[];
  hasUncheckedClasses: boolean;
  isEmpty: boolean;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  selectedTab?: ClassroomSelectedTab;
  getClassroomsByCourse: (courseId: string) => Classroom[];
  getYearUpdateCourse: (classData: Classroom) => string | undefined;
  setSelectedTab: (tab: ClassroomSelectedTab) => void;
  tabs: TabType[];
};

export function useSchoolClassroomsData(): UseSchoolClassroomsDataReturn {
  const { user } = useUser();
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { classroomsQuery } = useClassrooms({
    mine: false,
  });

  const {
    data: classroomsData,
    isLoading: isClassroomsLoading,
    isSuccess: isClassroomsSuccess,
    isError: isClassroomsError,
  } = classroomsQuery;

  const [selectedTab, setSelectedTab] = useState<
    ClassroomSelectedTab | undefined
  >();

  const { courses, groupCoursesByStage, getYearUpdateCourse } = useCourses({
    organizationId: user?.organizationId!,
    regionCode: user?.region!,
  });

  const classrooms = classroomsData?.classrooms as Classroom[];

  const filteredClassrooms = useMemo((): Classroom[] => {
    if (!classrooms) return [];

    return filterAndSortClassrooms(classrooms, CLASSROOM_TYPE.CLASSROOM);
  }, [classrooms]);

  const availableStages = useMemo(
    () =>
      Array.from(
        new Set(
          classrooms
            ?.sort((a, b) => a?.courseOrder! - b?.courseOrder!)
            .map((classroom) =>
              stages.getStageFromOrder(classroom?.courseOrder)
            )
            .filter((stage): stage is Stage => stage !== null)
        )
      ),
    [classrooms]
  );

  const createTabs = useCallback(() => {
    const stageTabs = availableStages.map((stage) => ({
      id: stage,
      text: t(`common.stage-${stage?.toLocaleLowerCase()}`),
      onClick: () => {
        setSelectedTab(stage as ClassroomSelectedTab);
      },
      selected: selectedTab === stage,
      dataTestId: `classrooms-stages-${stage}`,
    }));

    return stageTabs;
  }, [availableStages, selectedTab]);

  const hasTab = (tab: string): boolean => {
    return tabs.some((currentTab) => tab === currentTab?.id);
  };

  const tabs = createTabs();

  const getClassroomsByCourse = useCallback(
    (courseId: string): Classroom[] =>
      sortClassrooms(
        filteredClassrooms.filter(
          (classroom) => classroom?.courseId === courseId
        )
      ) as Classroom[],
    [filteredClassrooms]
  );

  const groupedCourses = useMemo(
    () => groupCoursesByStage(courses),
    [courses, groupCoursesByStage]
  );

  const isEmpty =
    !isClassroomsLoading && isClassroomsSuccess && classrooms?.length === 0;

  const hasUncheckedClasses = classrooms?.some((classroom) => {
    return (
      !classroom.checked || (classroom.provider && !classroom.providerChecked)
    );
  });

  useEffect(() => {
    if (selectedTab || tabs.length === 0) return;

    const selectedTabFromUrl = queryParams.get('stage');

    if (selectedTabFromUrl && hasTab(selectedTabFromUrl)) {
      setSelectedTab(selectedTabFromUrl as ClassroomSelectedTab);
    } else if (!selectedTab || !hasTab(selectedTab)) {
      setSelectedTab(tabs[0]!.id as ClassroomSelectedTab);
    }

    navigate({ search: '' }, { replace: true });
  }, [tabs]);

  return {
    filteredClassrooms,
    groupedCourses,
    hasUncheckedClasses,
    isEmpty,
    isError: isClassroomsError,
    isLoading: isClassroomsLoading,
    isSuccess: isClassroomsSuccess,
    selectedTab,
    getClassroomsByCourse,
    getYearUpdateCourse,
    setSelectedTab,
    tabs,
  };
}

export { CLASSROOM_TABS };
export type { ClassroomSelectedTab };
